import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Hero from '../components/Hero';
import ServiceCard from '../components/ServiceCard';
import TestimonialCard from '../components/TestimonialCard';
import FeatureCard from '../components/FeatureCard';
import DrillingCalculator from '../components/DrillingCalculator';
import { FaTools, FaGem, FaBomb, FaAward, FaShieldAlt, FaLeaf, FaClock, FaHeadset, FaCogs, FaSearch, FaClipboardCheck, FaHardHat, FaFlagCheckered, FaArrowRight } from 'react-icons/fa';
import './Home.css';

function Home() {
  const services = [
    {
      id: 1,
      icon: FaTools,
      title: 'Construction Technology',
      description: 'We integrate advanced technological solutions into the construction industry to improve efficiency, safety, and sustainability. Our systems streamline construction processes, reduce costs, enhance collaboration, and deliver higher quality projects while minimizing environmental impact.',
      features: ['Process Optimization', 'Safety Management', 'Cost Reduction', 'Quality Assurance']
    },
    {
      id: 2,
      icon: FaGem,
      title: 'Mining & Mining Support',
      description: 'We provide essential assistance to the mining industry with specialized services to enhance mining operations. Our expertise includes drilling, blasting, equipment maintenance, transportation, and technical consultation for exploration and sustainable operations.',
      features: ['Drilling Services', 'Equipment Maintenance', 'Logistics Support', 'Environmental Compliance']
    },
    {
      id: 3,
      icon: FaBomb,
      title: 'Rock Drilling & Blasting',
      description: 'We are knowledgeable in geology, explosives handling, and engineering principles of controlled blasting. Our work is critical in mining, construction, and infrastructure projects, ensuring efficient rock removal while minimizing risks.',
      features: ['Precision Blasting', 'Geological Analysis', 'Risk Management', 'Regulatory Compliance']
    }
  ];

  const features = [
    { icon: FaAward, title: 'Expert Team', description: 'Highly trained professionals with years of industry experience' },
    { icon: FaShieldAlt, title: 'Safety First', description: 'Strict adherence to safety and regulatory standards' },
    { icon: FaLeaf, title: 'Sustainability', description: 'Environmental responsibility in all our operations' },
    { icon: FaClock, title: 'On-Time Delivery', description: 'Projects completed within schedule and budget' },
    { icon: FaHeadset, title: '24/7 Support', description: 'Responsive and professional customer service' },
    { icon: FaCogs, title: 'Cutting-Edge Tech', description: 'Latest technology and innovative solutions' }
  ];

  const testimonials = [
    {
      id: 1,
      name: 'Chris Emmanuel',
      title: 'CEO, Harbock Homes Ltd',
      rating: 4.6,
      text: 'Working with C.U Fredson was an absolute game-changer for our project. Their rock drilling and blasting expertise was evident from day one, delivering precise and efficient results that exceeded our expectations. The team was professional, safety-conscious, and incredibly responsive to our needs.',
      image: null
    },
    {
      id: 2,
      name: 'Johnson Michael',
      title: 'CEO, Refield Global Real Estate',
      rating: 4.6,
      text: 'C.U Fredson truly impressed us with their rock drilling and blasting services. From the start, their professionalism and attention to detail were evident. They handled the job with remarkable precision, ensuring that everything was done safely and efficiently.',
      image: null
    },
    {
      id: 3,
      name: 'Director, Infrastructure Firm',
      title: 'Infrastructure Development',
      rating: 4.6,
      text: 'Excellent service from start to finish. The C.U Fredson team demonstrated exceptional knowledge of their field and maintained the highest standards of professionalism. We appreciated their detailed communication throughout the project.',
      image: null
    }
  ];

  const stats = [
    { label: 'Projects Completed', value: '100+' },
    { label: 'Expert Team Members', value: '50+' },
    { label: 'Years Experience', value: '10+' },
    { label: 'Client Rating', value: '4.6/5' }
  ];

  const processSteps = [
    { icon: FaSearch, number: '01', title: 'Understand the ground', description: 'We study your site, constraints, and project goals before any work begins.' },
    { icon: FaClipboardCheck, number: '02', title: 'Plan with precision', description: 'Our team develops a clear, compliant method built around safety and efficiency.' },
    { icon: FaHardHat, number: '03', title: 'Execute responsibly', description: 'Experienced crews and proven equipment keep work controlled and on schedule.' },
    { icon: FaFlagCheckered, number: '04', title: 'Deliver with confidence', description: 'We close every project with clear communication and dependable results.' }
  ];

  return (
    <main className="home">
      <Hero />

      <DrillingCalculator />
      
      {/* About Section */}
      <section className="section about-section" style={{ backgroundColor: 'var(--light-color)' }}>
        <div className="container">
          <div className="section-header">
            <h2>About C.U Fredson</h2>
            <p>Registered in Nigeria under the Companies and Allied Matters Act of 1990</p>
          </div>
          
          <div className="about-content">
            <div className="about-text">
              <p>We are committed to providing innovative solutions to peculiar Geological, Environmental, and Construction needs across Nigeria. Our mission is to integrate advanced technological solutions into industries to improve efficiency, safety, and sustainability.</p>
              
              <div className="stats-grid">
                {stats.map(stat => (
                  <div key={stat.label} className="stat-card">
                    <h3>{stat.value}</h3>
                    <p>{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="about-image">
              <div className="image-placeholder">
                <img src="/images/Fredsonimg 5.jpg" alt="C.U Fredson engineering work site" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section" style={{ backgroundColor: 'white' }}>
        <div className="container">
          <div className="section-header">
            <h2>Our Services</h2>
            <p>Comprehensive engineering solutions for modern industry challenges</p>
          </div>

          <div className="grid grid-3">
            {services.map(service => (
              <ServiceCard key={service.id} service={service} />
            ))}
          </div>
        </div>
      </section>

      {/* Delivery Process */}
      <section className="section process-section">
        <div className="container">
          <motion.div
            className="process-heading"
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.35 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="process-heading-copy">
              <p className="home-label">How we work</p>
              <h2>From first brief<br />to finished ground.</h2>
            </div>
            <p>A considered process keeps every moving part aligned, from the first site conversation to the final result.</p>
          </motion.div>

          <div className="process-grid">
            {processSteps.map(step => {
              const Icon = step.icon;
              return (
                <motion.article
                  className="process-step"
                  key={step.number}
                  initial={{ opacity: 0, y: 34 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{
                    duration: 0.65,
                    delay: Number(step.number) * 0.1,
                    ease: [0.22, 1, 0.36, 1]
                  }}
                >
                  <div className="process-step-top"><span>{step.number}</span><Icon /></div>
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </motion.article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Project Proof */}
      <section className="project-proof">
        <div className="container project-proof-inner">
          <div>
            <p className="home-label">Experience that travels</p>
            <h2>Built across Nigeria.<br /><em>Ready for your terrain.</em></h2>
          </div>
          <div className="project-proof-copy">
            <p>From tunnel excavation in Ebonyi to infrastructure and quarry work across the country, our project record reflects range, discipline, and local knowledge.</p>
            <Link to="/projects" className="proof-link">View project experience <FaArrowRight /></Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section" style={{ backgroundColor: 'var(--light-color)' }}>
        <div className="container">
          <div className="section-header">
            <h2>Why Choose C.U Fredson</h2>
            <p>Setting the standard for engineering excellence</p>
          </div>

          <div className="grid grid-3">
            {features.map((feature, index) => (
              <FeatureCard key={index} feature={feature} />
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section" style={{ backgroundColor: 'var(--light-color)' }}>
        <div className="container">
          <div className="section-header">
            <h2>What Our Clients Say</h2>
            <p>Real feedback from satisfied partners</p>
          </div>

          <div className="grid grid-3">
            {testimonials.map(testimonial => (
              <TestimonialCard key={testimonial.id} testimonial={testimonial} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Partner With Us?</h2>
            <p>Let's discuss how C.U Fredson can help bring your project to success</p>
            <Link to="/contact" className="btn btn-light">Get in Touch Today</Link>
          </div>
        </div>
      </section>
    </main>
  );
}

export default Home;
